import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Download, X, FileText } from 'lucide-react';
import IncomeStatementSlide2 from './reports/IncomeStatementSlide2';
import IncomeStatementSlide3 from './reports/IncomeStatementSlide3';
import IncomeStatementSlide4 from './reports/IncomeStatementSlide4';
import IncomeStatementSlide5 from './reports/IncomeStatementSlide5';
import LiquidityReportPreview from './reports/LiquidityReportPreview';
import { 
  getPerformanceFinanceiraData, 
  getSlide2Data, 
  getSlide3Data, 
  getSlide4Data 
} from '../data/reportDataSource';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportPreview = ({ 
  reportData, 
  reportId = 'performance_financeira',
  language = 'pt', 
  onClose, 
  onDownload,
  isDownloading = false 
}) => {
  // If this is a liquidity report, use the dedicated component
  if (reportId === 'liquidez_fluxo_caixa') {
    return (
      <LiquidityReportPreview
        reportData={reportData}
        language={language}
        onClose={onClose}
        onDownload={onDownload}
        isDownloading={isDownloading}
      />
    );
  }

  // Default: Performance Financeira / Demonstração de Resultados
  return (
    <DefaultReportPreview
      reportData={reportData}
      language={language}
      onClose={onClose}
      onDownload={onDownload}
      isDownloading={isDownloading}
    />
  );
};

// Original ReportPreview component renamed to DefaultReportPreview
const DefaultReportPreview = ({ 
  reportData, 
  language = 'pt', 
  onClose, 
  onDownload,
  isDownloading = false 
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [waterfallData, setWaterfallData] = useState(null);
  const [slidesData, setSlidesData] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const totalSlides = 5;

  // Fetch all slides data
  useEffect(() => {
    const fetchAllSlidesData = async () => {
      try {
        setIsLoadingData(true);
        
        // Fetch unified slides data
        const slidesResponse = await fetch(`${API_URL}/api/executive-reports/slides-data/1T26`);
        if (slidesResponse.ok) {
          const slidesResult = await slidesResponse.json();
          if (slidesResult.success && slidesResult.data) {
            setSlidesData(slidesResult.data);
          }
        }
        
        // Also fetch waterfall drivers for backward compatibility
        const waterfallResponse = await fetch(`${API_URL}/api/executive-reports/waterfall-drivers/1T26`);
        if (waterfallResponse.ok) {
          const waterfallResult = await waterfallResponse.json();
          if (waterfallResult.success && waterfallResult.data) {
            setWaterfallData(waterfallResult.data);
          }
        }
      } catch (error) {
        console.warn('Failed to load slides data, using defaults:', error);
      } finally {
        setIsLoadingData(false);
      }
    };
    
    fetchAllSlidesData();
  }, []);

  // Translations
  const t = language === 'en' ? {
    executiveReport: 'Executive Report',
    incomeStatement: 'Income Statement',
    executive: 'Executive',
    companyName: 'Company ABC',
    reportDescription: 'Executive report on financial and operational performance',
    periodLabel: 'Period under analysis: Q1 2026',
    executiveThesisLabel: 'EXECUTIVE THESIS',
    executiveThesis: 'Q1 2026 confirms operational acceleration with EBITDA expansion and strong cash conversion, despite price/mix pressure.',
    managementView: 'Management View',
    cashFocus: 'Cash Focus',
    financialDiscipline: 'Financial Discipline',
    bullet1: 'Integrates financial and operational analysis, focusing on revenue, margin, cash and execution.',
    bullet2: 'Structured for the Board of Directors and financial stakeholders.',
    bullet3: 'Includes performance, risk factors and monitoring priorities.',
    executiveReadingTitle: 'EXECUTIVE READING Q1 2026',
    kpiSectionTitle: 'Key Executive Indicators Q1 2026',
    growthWithScale: 'Growth with scale',
    growthText: 'Revenue reaches USD {revenue}M, supported by higher marketable volume (+5% vs Budget; +24% vs Q1 25).',
    marginExpansion: 'Margin expansion',
    marginText: 'EBITDA of USD {ebitda}M and margin of {margin}% confirm profitability expansion despite price/mix pressure.',
    cashDiscipline: 'Cash discipline',
    cashText: 'OCF of USD {fco}M and Net Debt/EBITDA of {dl}x show strong cash conversion and balance sheet comfort.',
    sales: 'SALES',
    ebitda: 'EBITDA',
    ebitdaMargin: 'EBITDA MARGIN',
    netIncome: 'NET INCOME',
    ocf: 'OPERATING CASH FLOW',
    netDebtEbitda: 'NET DEBT / EBITDA',
    vsBudget: 'vs Budget Q1 26',
    vsPrior: 'vs Q1 25',
    executiveSynthesis: 'EXECUTIVE MANAGEMENT SYNTHESIS',
    synthesis1: 'The quarter shows growth converted into EBITDA and cash, maintaining balance sheet comfort.',
    synthesis2: 'Value creation comes mainly from volume and efficiency; price/mix remains the main watchpoint.',
    synthesis3: 'Management priority is to preserve operational discipline and protect cash conversion as ramp-up continues.',
    footerLeft: 'ABC | Executive Income Statement | Final presentation version',
    boardReady: 'Board-ready',
    slideOf: 'of',
    downloadPdf: 'Download PDF',
    close: 'Close',
    executiveHeadline: 'Executive Headline',
    executiveSummary: 'Executive Summary',
    resultFormation: 'Result Formation',
    closing: 'Closing & Next Steps',
  } : {
    executiveReport: 'Relatório Executivo',
    incomeStatement: 'Demonstração de Resultados',
    executive: 'Executiva',
    companyName: 'Company ABC',
    reportDescription: 'Relatório executivo de desempenho financeiro e operacional',
    periodLabel: 'Período em análise: 1.º trimestre de 2026',
    executiveThesisLabel: 'TESE EXECUTIVA DO PERÍODO',
    executiveThesis: '1T26 confirma aceleração operacional com expansão de EBITDA e forte conversão em caixa, apesar da pressão no preço/mix.',
    managementView: 'Visão de Gestão',
    cashFocus: 'Foco Caixa',
    financialDiscipline: 'Disciplina Financeira',
    bullet1: 'Integra leitura financeira e operacional, com foco em receita, margem, caixa e execução.',
    bullet2: 'Estruturado para o Conselho de Gerência e stakeholders financeiros.',
    bullet3: 'Inclui desempenho, factores de risco e prioridades de monitorização.',
    executiveReadingTitle: 'LEITURA EXECUTIVA DO 1º TRIMESTRE 2026',
    kpiSectionTitle: 'Indicadores Executivos-Chave do 1º Trimestre 2026',
    growthWithScale: 'Crescimento com escala',
    growthText: 'Receita atinge USD {revenue}M, suportada por maior volume comercializável (+5% vs Orç.; +24% vs 1T25).',
    marginExpansion: 'Margem em expansão',
    marginText: 'EBITDA de USD {ebitda}M e margem de {margin}% confirmam expansão da rentabilidade apesar da pressão no preço/mix.',
    cashDiscipline: 'Caixa com disciplina',
    cashText: 'FCO de USD {fco}M e DL/EBITDA de {dl}x evidenciam forte conversão em caixa e conforto de balanço.',
    sales: 'VENDAS',
    ebitda: 'EBITDA',
    ebitdaMargin: 'MARGEM EBITDA',
    netIncome: 'RESULTADO LÍQUIDO',
    ocf: 'FLUXO DE CAIXA OPERACIONAL',
    netDebtEbitda: 'DÍVIDA LÍQ. / EBITDA',
    vsBudget: 'vs Orç. 1T26',
    vsPrior: 'vs 1T25',
    executiveSynthesis: 'SÍNTESE EXECUTIVA DE GESTÃO',
    synthesis1: 'O trimestre mostra crescimento convertido em EBITDA e caixa, com manutenção de conforto de balanço.',
    synthesis2: 'A criação de valor resulta sobretudo de volume e eficiência; preço/mix continuam a ser o principal ponto de vigilância.',
    synthesis3: 'A prioridade de gestão é preservar disciplina operacional e proteger conversão em caixa à medida que o ramp-up continua.',
    footerLeft: 'ABC | Demonstração de Resultados Executiva | Versão final de apresentação',
    boardReady: 'Board-ready',
    slideOf: 'de',
    downloadPdf: 'Descarregar PDF',
    close: 'Fechar',
    executiveHeadline: 'Headline Executiva',
    executiveSummary: 'Resumo Executivo',
    resultFormation: 'Formação do Resultado',
    closing: 'Fecho e Próximos Passos',
  };

  // ============ GET DATA FROM CENTRALIZED SOURCE ============
  const reportDataFromSource = getPerformanceFinanceiraData('Q1_2026');
  
  // Use centralized data
  const data = {
    revenue: reportDataFromSource.revenue,
    ebitda: reportDataFromSource.ebitda,
    ebitdaMargin: reportDataFromSource.ebitdaMargin,
    netIncome: Math.abs(reportDataFromSource.netIncome),
    isNetIncomeLoss: reportDataFromSource.isNetIncomeLoss,
    fco: 1.6, // From cash flow data
    dlEbitda: 0.8,
    // Formatted variations
    vsOrcRevenue: reportDataFromSource.vsOrcRevenue,
    vsOrcEbitda: reportDataFromSource.vsOrcEbitda,
    vsOrcMargin: reportDataFromSource.vsOrcMargin,
    vsOrcNetIncome: reportDataFromSource.vsOrcNetIncome,
    vs1T25Revenue: reportDataFromSource.vs1T25Revenue,
    vs1T25Ebitda: reportDataFromSource.vs1T25Ebitda,
    vs1T25Margin: reportDataFromSource.vs1T25Margin,
    vs1T25NetIncome: reportDataFromSource.vs1T25NetIncome,
  };

  // Determine if variations are positive (for styling)
  const isPositive = (variation) => {
    if (typeof variation === 'string') {
      return variation.startsWith('+');
    }
    return variation >= 0;
  };

  const kpis = [
    { label: t.sales, value: `USD ${data.revenue.toFixed(1)}M`, vsOrc: data.vsOrcRevenue, vs1T25: data.vs1T25Revenue, positive: isPositive(data.vsOrcRevenue) },
    { label: t.ebitda, value: `USD ${data.ebitda.toFixed(1)}M`, vsOrc: data.vsOrcEbitda, vs1T25: data.vs1T25Ebitda, positive: isPositive(data.vsOrcEbitda) },
    { label: t.ebitdaMargin, value: `${data.ebitdaMargin.toFixed(1)}%`, vsOrc: data.vsOrcMargin, vs1T25: data.vs1T25Margin, positive: isPositive(data.vsOrcMargin) },
    { label: t.netIncome, value: `USD ${data.isNetIncomeLoss ? '-' : ''}${data.netIncome.toFixed(1)}M`, vsOrc: data.vsOrcNetIncome, vs1T25: data.vs1T25NetIncome, positive: isPositive(data.vsOrcNetIncome) },
    { label: t.ocf, value: `USD ${data.fco.toFixed(1)}M`, vsOrc: '+12%', vs1T25: '+34%', positive: true },
    { label: t.netDebtEbitda, value: `${data.dlEbitda}x`, vsOrc: '-0.2x', vs1T25: '-0.1x', positive: true, lowerBetter: true },
  ];

  const cards = [
    { title: t.growthWithScale, text: t.growthText.replace('{revenue}', data.revenue.toFixed(1)) },
    { title: t.marginExpansion, text: t.marginText.replace('{ebitda}', data.ebitda.toFixed(1)).replace('{margin}', data.ebitdaMargin.toFixed(1)) },
    { title: t.cashDiscipline, text: t.cashText.replace('{fco}', data.fco.toFixed(1)).replace('{dl}', data.dlEbitda) },
  ];

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' && currentSlide < totalSlides - 1) {
        setCurrentSlide(prev => prev + 1);
      } else if (e.key === 'ArrowLeft' && currentSlide > 0) {
        setCurrentSlide(prev => prev - 1);
      } else if (e.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, onClose]);

  // Slide 1: Cover
  const CoverSlide = () => (
    <div className="w-full h-full bg-white rounded-lg overflow-hidden flex flex-col">
      <div className="flex-1 flex">
        {/* Left Side - Navy (exact PDF color) */}
        <div className="w-[42%] bg-[#0A192F] p-6 flex flex-col">
          {/* Kicker */}
          <p className="text-[#C8A24A] font-semibold text-xs mb-3">{t.executiveReport}</p>
          
          {/* Title */}
          <h1 className="text-white font-serif text-2xl font-bold leading-tight mb-0.5">
            {t.incomeStatement}
          </h1>
          <h1 className="text-white font-serif text-2xl font-bold mb-4">{t.executive}</h1>
          
          {/* Entity */}
          <p className="text-white font-semibold text-xs mb-0.5">{t.companyName}</p>
          <p className="text-gray-400 text-[10px] mb-3">{t.reportDescription}</p>
          
          {/* Period */}
          <p className="text-[#D6BE7A] text-xs">{t.periodLabel}</p>
          
          {/* Thesis Block */}
          <div className="mt-4 border-l-2 border-[#C8A24A] pl-3">
            <p className="text-[#D6BE7A] text-[10px] font-semibold uppercase mb-1">{t.executiveThesisLabel}</p>
            <p className="text-white italic text-xs leading-relaxed">{t.executiveThesis}</p>
          </div>
          
          {/* Tags */}
          <div className="flex gap-1.5 mt-4">
            {[t.managementView, t.cashFocus, t.financialDiscipline].map((tag, i) => (
              <span key={i} className="bg-[#F5F3EB] text-[#0A192F] text-[10px] font-semibold px-2 py-1.5 rounded">
                {tag}
              </span>
            ))}
          </div>
          
          {/* Bullets - 1cm below tags */}
          <div className="space-y-1.5 mt-8">
            {[t.bullet1, t.bullet2, t.bullet3].map((bullet, i) => (
              <p key={i} className="text-gray-400 text-[10px] flex gap-1.5 leading-relaxed">
                <span className="text-[#D6BE7A]">•</span>
                {bullet}
              </p>
            ))}
          </div>
        </div>
        
        {/* Right Side - Light */}
        <div className="w-[58%] bg-[#F7F6F2] p-5 flex flex-col">
          {/* Section Label */}
          <p className="text-[#64748B] font-semibold text-xs mb-3">{t.executiveReadingTitle}</p>
          
          {/* Message Cards */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            {cards.map((card, i) => (
              <div key={i} className="bg-white rounded-lg p-2.5 border-t-3 border-[#C5A059]" style={{borderTopWidth: '3px'}}>
                <p className="text-[#0A192F] font-bold text-[10px] mb-1.5">{card.title}</p>
                <p className="text-[#4A5568] text-[9px] leading-relaxed">{card.text}</p>
              </div>
            ))}
          </div>
          
          {/* KPI Section Label */}
          <p className="text-[#64748B] font-semibold text-xs mb-2">{t.kpiSectionTitle}</p>
          
          {/* KPI Grid */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            {kpis.map((kpi, i) => (
              <div key={i} className="bg-white rounded-lg p-2.5 border border-[#E2E8F0]">
                <p className="text-[#6B778C] text-[9px] uppercase mb-0.5">{kpi.label}</p>
                <p className="text-[#0A192F] font-bold text-lg mb-1.5">{kpi.value}</p>
                <div className="flex gap-3 text-[9px]">
                  <div>
                    <span className="text-[#64748B]">{t.vsBudget}</span>
                    <p className={`font-bold ${kpi.positive ? 'text-emerald-500' : 'text-red-500'}`}>{kpi.vsOrc}</p>
                  </div>
                  <div>
                    <span className="text-[#64748B]">{t.vsPrior}</span>
                    <p className={`font-bold ${kpi.positive ? 'text-emerald-500' : 'text-red-500'}`}>{kpi.vs1T25}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Synthesis Block */}
          <div className="bg-[#F0EEE9] rounded-lg p-3 border-l-3 border-[#C5A059] flex-1" style={{borderLeftWidth: '3px'}}>
            <p className="text-[#0A192F] font-bold text-xs mb-2">{t.executiveSynthesis}</p>
            <div className="space-y-1.5">
              {[t.synthesis1, t.synthesis2, t.synthesis3].map((line, i) => (
                <p key={i} className="text-[#1E293B] text-[10px] leading-relaxed">{line}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="flex justify-between items-center px-6 py-2 border-t border-gray-200 text-[10px] bg-white">
        <span className="text-[#64748B]">{t.footerLeft}</span>
        <span className="text-[#C5A059] font-semibold">{t.boardReady}</span>
        <span className="text-[#64748B]">1 / {totalSlides}</span>
      </div>
    </div>
  );

  // Generic content slide
  const ContentSlide = ({ title, slideNum }) => (
    <div className="w-full h-full bg-white rounded-lg p-8 flex flex-col">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-1 h-8 bg-[#C5A059] rounded-full"></div>
        <h2 className="text-[#0A192F] font-bold text-2xl">{title}</h2>
      </div>
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-[#64748B]">
          <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg">{language === 'pt' ? 'Conteúdo do slide' : 'Slide content'} {slideNum}</p>
          <p className="text-sm mt-2">{language === 'pt' ? 'Dados dinâmicos serão apresentados aqui' : 'Dynamic data will be displayed here'}</p>
        </div>
      </div>
      {/* Footer */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-200 text-[10px] text-[#64748B]">
        <span>{t.footerLeft}</span>
        <span className="text-[#C5A059] font-semibold">{t.boardReady}</span>
        <span>{slideNum} / {totalSlides}</span>
      </div>
    </div>
  );

  // Closing slide with download - Institutional version
  // Slide 2 data - EBITDA formation with TWO main comparisons
  // Uses dynamic data from API when available, falls back to defaults
  const slide2Data = waterfallData ? {
    // Comparison 1: 1T25 -> 1T26 (from API)
    ebitda1T25: waterfallData.ebitda['1t25'],
    ebitda1T26: waterfallData.ebitda['1t26'],
    drivers1T25to1T26: {
      volRegulares: waterfallData.yoy_drivers.vol_regulares,
      volEspeciais: waterfallData.yoy_drivers.vol_especiais,
      precoRegulares: waterfallData.yoy_drivers.preco_regulares,
      precoEspeciais: waterfallData.yoy_drivers.preco_especiais,
      efficiency: waterfallData.yoy_drivers.eficiencia,
      outros: waterfallData.yoy_drivers.outros,
    },
    // Comparison 2: Real 1T26 vs Orç. 1T26 (from API)
    ebitdaOrc1T26: waterfallData.ebitda.orc_1t26,
    driversVsBudget: {
      volRegulares: waterfallData.budget_drivers.vol_regulares,
      volEspeciais: waterfallData.budget_drivers.vol_especiais,
      precoRegulares: waterfallData.budget_drivers.preco_regulares,
      precoEspeciais: waterfallData.budget_drivers.preco_especiais,
      efficiency: waterfallData.budget_drivers.eficiencia,
      outros: waterfallData.budget_drivers.outros,
    },
    // Cards data (from API)
    cards: {
      volRegulares: {
        metric: waterfallData.driver_cards[0].metric,
        vs1T25: waterfallData.driver_cards[0].vs1T25,
        vsOrc: waterfallData.driver_cards[0].vsOrc,
      },
      volEspeciais: {
        metric: waterfallData.driver_cards[1].metric,
        vs1T25: waterfallData.driver_cards[1].vs1T25,
        vsOrc: waterfallData.driver_cards[1].vsOrc,
      },
      precoRegulares: {
        metric: waterfallData.driver_cards[2].metric,
        vs1T25: waterfallData.driver_cards[2].vs1T25,
        vsOrc: waterfallData.driver_cards[2].vsOrc,
      },
      precoEspeciais: {
        metric: waterfallData.driver_cards[3].metric,
        vs1T25: waterfallData.driver_cards[3].vs1T25,
        vsOrc: waterfallData.driver_cards[3].vsOrc,
      },
    },
    // Bottom strip (from API)
    vsBudget: {
      ebitdaVar: waterfallData.summary_strip.ebitda_vs_orc,
      mainPositive: waterfallData.summary_strip.main_positive,
      mainPressure: waterfallData.summary_strip.main_pressure,
    },
  } : {
    // Default fallback data
    ebitda1T25: 20.2, 
    ebitda1T26: 30.1,
    drivers1T25to1T26: { 
      volRegulares: 4.2, 
      volEspeciais: 3.1, 
      precoRegulares: -2.8, 
      precoEspeciais: -1.4, 
      efficiency: 3.8, 
      outros: 3.2 
    },
    ebitdaOrc1T26: 25.9,
    driversVsBudget: { 
      volRegulares: 1.4, 
      volEspeciais: 1.2, 
      precoRegulares: -0.8, 
      precoEspeciais: -0.6, 
      efficiency: 1.8, 
      outros: 1.2 
    },
    cards: {
      volRegulares: { metric: '124 k ct', vs1T25: 18.2, vsOrc: 4.8 },
      volEspeciais: { metric: '32 k ct', vs1T25: 42.5, vsOrc: 8.1 },
      precoRegulares: { metric: 'USD 285', vs1T25: -6.2, vsOrc: -3.2 },
      precoEspeciais: { metric: 'USD 1,840', vs1T25: -4.8, vsOrc: -2.1 },
    },
    vsBudget: { ebitdaVar: 16.2, mainPositive: 'Volume acima do orçamento', mainPressure: 'Preço/mix abaixo do orçamento' },
  };

  // Slide 3 data - Margin Formation (use dynamic data or centralized source)
  const slide3Data = slidesData?.slide3 || getSlide3Data('Q1_2026');

  // Slide 4 data - Cash Conversion (use dynamic data or centralized source)
  const slide4Data = slidesData?.slide4 || getSlide4Data('Q1_2026');

  // Slide 5 data - Executive Summary (use dynamic data if available)
  const slide5Data = slidesData?.slide5 || {
    ebitdaGrowth: '+49%',
    marginExpansion: '+3.8 pp',
    cashConversion: '84%',
  };

  const slides = [
    <CoverSlide key="cover" />,
    <div key="formation" className="w-full h-full flex items-center justify-center bg-[#0A192F] overflow-hidden">
      <div className="transform scale-[0.56] origin-center">
        <IncomeStatementSlide2 data={slide2Data} language={language} />
      </div>
    </div>,
    <div key="margin" className="w-full h-full flex items-center justify-center bg-[#0A192F] overflow-hidden">
      <div className="transform scale-[0.56] origin-center">
        <IncomeStatementSlide3 data={slide3Data} language={language} />
      </div>
    </div>,
    <div key="cash" className="w-full h-full flex items-center justify-center bg-[#0A192F] overflow-hidden">
      <div className="transform scale-[0.56] origin-center">
        <IncomeStatementSlide4 data={slide4Data} language={language} />
      </div>
    </div>,
    <div key="summary" className="w-full h-full flex items-center justify-center bg-[#0A192F] overflow-hidden">
      <div className="transform scale-[0.56] origin-center">
        <IncomeStatementSlide5 data={slide5Data} language={language} />
      </div>
    </div>,
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-all"
      >
        <X className="w-6 h-6" />
      </button>
      
      {/* Slide counter */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
        {currentSlide + 1} {t.slideOf} {totalSlides}
      </div>
      
      {/* Navigation dots and Download button */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4">
        <div className="flex gap-2">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`w-2 h-2 rounded-full transition-all ${
                i === currentSlide ? 'bg-[#C5A059] w-6' : 'bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
        {/* Download PDF button */}
        <button 
          onClick={onDownload} 
          disabled={isDownloading}
          className="flex items-center gap-2 bg-[#C5A059] hover:bg-[#B8934D] text-white font-semibold px-6 py-2.5 rounded-lg transition-all disabled:opacity-50 ml-4"
        >
          <Download className="w-4 h-4" />
          {isDownloading ? (language === 'pt' ? 'A gerar...' : 'Generating...') : 'PDF'}
        </button>
      </div>
      
      {/* Left arrow */}
      <button
        onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
        disabled={currentSlide === 0}
        className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="w-8 h-8" />
      </button>
      
      {/* Right arrow */}
      <button
        onClick={() => setCurrentSlide(prev => Math.min(totalSlides - 1, prev + 1))}
        disabled={currentSlide === totalSlides - 1}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ChevronRight className="w-8 h-8" />
      </button>
      
      {/* Slide container */}
      <div className="w-full max-w-5xl aspect-[16/9] relative overflow-hidden rounded-xl shadow-2xl">
        <div 
          className="flex transition-transform duration-500 ease-out h-full"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slides.map((slide, i) => (
            <div key={i} className="w-full h-full flex-shrink-0">
              {slide}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReportPreview;
